import React, { useState, useEffect, useRef, useCallback } from 'react';
import util from '../../styles/utilities.module.css';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { 
  useGetBadgeModuleQuery, 
  useSubmitModuleQuizMutation,
  useUpdateContentProgressMutation
} from '../../slices/credentialingApi';
import { formatDuration } from '../../utils/credentialingUtils';
import ModuleQuiz from './ModuleQuiz.jsx';
import VideoProgressTracker from './VideoProgressTracker.jsx';
import ReflectionSubmission from './ReflectionSubmission.jsx';
import QuizResults from './QuizResults.jsx';
import './BadgeModule.css';
import InfoModal from '../../common/InfoModal.jsx';

// Section-specific skeletons to improve perceived loading
const ModuleSkeleton = ({ section }) => {
  const showVideo = !section || section === 'video' || section === 'all';
  const showContent = !section || section === 'content' || section === 'all';
  const showQuiz = !section || section === 'quiz' || section === 'all';

  return (
    <div className="module-skeleton">
      {/* Keep original loading string for tests & screen readers */}
      <p style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>Loading training module...</p>
      {/* Header skeleton mirrors layout */}
      <div className="training-module__header">
        <div className="header-navigation">
          <div className="skeleton skeleton-pill sm" />
          <div className="module-meta">
            <div className="skeleton skeleton-pill xs" />
            <div className="skeleton skeleton-pill xs" />
          </div>
        </div>
        <div className="skeleton skeleton-bar xl" />
        <div className="skeleton skeleton-bar md" />
        {/* Learning objectives block approximation */}
        <div className="learning-objectives">
          <div className="skeleton skeleton-bar sm" />
          <div className="skeleton skeleton-bar sm" />
          <div className="skeleton skeleton-bar sm" />
        </div>
      </div>

      {/* Video section skeleton */}
      {showVideo && (
        <div className="training-module__video">
          <div className="video-loading-placeholder">
            <div className="video-skeleton">
              <div className="skeleton-play-button">
                <span className="play-icon">▶</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content section skeleton */}
      {showContent && (
        <div className="training-module__content">
          <div className="module-content">
            {/* Simulate headings and paragraphs */}
            <div className="skeleton skeleton-bar lg" />
            <div className="skeleton skeleton-bar xl" />
            <div className="skeleton skeleton-bar lg" />
            <div className="skeleton skeleton-bar md" />
            <div className="skeleton skeleton-bar xl" />
            <div className="skeleton skeleton-bar md" />
          </div>
        </div>
      )}

      {/* Quiz section skeleton */}
      {showQuiz && (
        <div className="training-module__quiz-intro">
          <div className="quiz-intro-container">
            {/* Heading and subtext */}
            <div className="skeleton skeleton-bar md" />
            <div className="skeleton skeleton-bar sm" />
            {/* Mimic quiz status blocks with structure */}
            <div className="quiz-status">
              <span className="status-icon"><span className="skeleton skeleton-circle md" /></span>
              <div className="status-info">
                <p><span className="skeleton skeleton-bar sm" /></p>
                <p><span className="skeleton skeleton-bar xs" /></p>
              </div>
            </div>
            <div className="quiz-status">
              <span className="status-icon"><span className="skeleton skeleton-circle md" /></span>
              <div className="status-info">
                <p><span className="skeleton skeleton-bar sm" /></p>
                <p><span className="skeleton skeleton-bar xs" /></p>
              </div>
            </div>
            {/* Buttons row */}
            <div className="skeleton-button-row">
              <div className="skeleton skeleton-button" />
              <div className="skeleton skeleton-button ghost" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BadgeModule = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section'); // 'video', 'quiz', 'content', or null
  
  const [showQuiz, setShowQuiz] = useState(false);

  // Time tracking for content-only modules (module-specific)
  const viewStartTime = useRef(null);
  const [showCompleteButton, setShowCompleteButton] = useState(false);
  const [viewingTime, setViewingTime] = useState(0);

  const { 
    data: moduleData, 
    isLoading, 
    error, 
    refetch 
  } = useGetBadgeModuleQuery(moduleId, {
    skip: !moduleId
  });

  const [submitQuiz] = useSubmitModuleQuizMutation();
  const [updateContentProgress] = useUpdateContentProgressMutation();
  const contentRef = useRef(null);
  const lastScrollSentRef = useRef(0); // last percentage sent to API
  const scrollThrottleRef = useRef(null);
  const [showReflectionModal, setShowReflectionModal] = useState(false);
  const hasShownReflectionModalRef = useRef(false);
  const [unlockedReflection, setUnlockedReflection] = useState(false);
  const reflectionModalTimeoutRef = useRef(null);

  // Helper: schedule reflection modal with delay (default 3s)
  const scheduleReflectionModal = useCallback((delayMs = 3000) => {
    if (hasShownReflectionModalRef.current) return;
    if (reflectionModalTimeoutRef.current) {
      clearTimeout(reflectionModalTimeoutRef.current);
    }
    reflectionModalTimeoutRef.current = setTimeout(() => {
      if (!hasShownReflectionModalRef.current) {
        hasShownReflectionModalRef.current = true;
        setShowReflectionModal(true);
      }
    }, delayMs);
  }, []);

  // Initialize and track viewing time for content-only modules
  useEffect(() => {
    const { module, progress } = moduleData?.data || {};
    
    // Only for content-only modules that aren't already completed
    if (module && progress && !progress.is_completed) {
      const isContentOnlyModule = !module.has_quiz && !module.has_video && !module.requires_reflection;
      
      if (isContentOnlyModule) {
        // Initialize start time for THIS specific module
        if (viewStartTime.current === null) {
          viewStartTime.current = Date.now();
        }
        
        // Update viewing time every second and show complete button after 10 seconds
        const interval = setInterval(() => {
          const currentViewingTime = Math.floor((Date.now() - viewStartTime.current) / 1000);
          setViewingTime(currentViewingTime);
          
          if (currentViewingTime >= 10 && !showCompleteButton) {
            setShowCompleteButton(true);
          }
        }, 1000);

        return () => clearInterval(interval);
      }
    }
  }, [moduleData, showCompleteButton]);

  // Reset timer when module changes
  useEffect(() => {
    viewStartTime.current = null;
    setShowCompleteButton(false);
    setViewingTime(0);
    lastScrollSentRef.current = 0;
    hasShownReflectionModalRef.current = false;
    setUnlockedReflection(false);
    if (reflectionModalTimeoutRef.current) {
      clearTimeout(reflectionModalTimeoutRef.current);
      reflectionModalTimeoutRef.current = null;
    }
  }, [moduleId]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (reflectionModalTimeoutRef.current) {
      clearTimeout(reflectionModalTimeoutRef.current);
    }
  }, []);

  // Compute scroll percentage of content revealed (bottom of viewport relative to content height)
  const computeScrollPercentage = useCallback(() => {
    const el = contentRef.current;
    if (!el) return 0;

    const fullHeight = el.scrollHeight || 1;
    const clientHeight = el.clientHeight || 1;
    const scrollableHeight = fullHeight - clientHeight;

    // If the element itself scrolls (e.g., modal with overflow), use its scroll position
    if (scrollableHeight > 4) {
      const elementScrollTop = el.scrollTop || 0;
      const elementProgress = ((elementScrollTop + clientHeight) / fullHeight) * 100;
      return Math.max(0, Math.min(100, elementProgress));
    }

    // Fallback to window scroll position
    const rect = el.getBoundingClientRect();
    const docEl = document.documentElement;
    const scrollTop = window.scrollY || docEl.scrollTop;
    const viewportHeight = window.innerHeight || docEl.clientHeight;
    const elementTop = scrollTop + rect.top;
    const visibleBottom = scrollTop + viewportHeight;
    const rawProgress = (visibleBottom - elementTop) / fullHeight;
    return Math.max(0, Math.min(100, rawProgress * 100));
  }, []);

  const sendScrollProgress = useCallback((pct) => {
    const clampedPct = Math.max(0, Math.min(100, pct));
    if (clampedPct - lastScrollSentRef.current < 5 && !(clampedPct >= 95 && lastScrollSentRef.current < 95)) return;
    lastScrollSentRef.current = clampedPct;
    const currentViewingTime = viewStartTime.current ? Math.floor((Date.now() - viewStartTime.current) / 1000) : 0;
    updateContentProgress({
      moduleId: parseInt(moduleId),
      scroll_percentage: Math.round(clampedPct * 10) / 10,
      reading_time: currentViewingTime
    }).catch(() => {});
  }, [moduleId, updateContentProgress]);

  // Attach scroll listener for modules where content matters
  useEffect(() => {
    const { module, progress } = moduleData?.data || {};
    if (!module) return;
    const isPureContent = !module.has_quiz && !module.has_video && !module.requires_reflection;
    const hasRenderableContent = Boolean(module.has_text_content || (module.content && module.content.trim()));
    const needsContentTracking = hasRenderableContent || isPureContent || module.content_required || module.completion_method === 'content';
    if (!needsContentTracking || progress?.is_completed) return;
    if (viewStartTime.current === null) viewStartTime.current = Date.now();

    const handleScroll = () => {
      if (scrollThrottleRef.current) return;
      scrollThrottleRef.current = requestAnimationFrame(() => {
        scrollThrottleRef.current = null;
        const pct = computeScrollPercentage();
        sendScrollProgress(pct);
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    const contentEl = contentRef.current;
    if (contentEl) {
      contentEl.addEventListener('scroll', handleScroll, { passive: true });
    }
    window.addEventListener('resize', handleScroll);
    const initialPct = computeScrollPercentage();
    if (initialPct > 0) sendScrollProgress(initialPct);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (contentEl) {
        contentEl.removeEventListener('scroll', handleScroll);
      }
      if (scrollThrottleRef.current) cancelAnimationFrame(scrollThrottleRef.current);
    };
  }, [moduleData, computeScrollPercentage, sendScrollProgress]);

  // Handle module completion from different methods
  const handleModuleCompletion = (completionMethod, data = {}) => {
    // Refetch module data to get updated completion status
    refetch();
    
    // Auto-scroll to top to show completion banner for video and reflection completions
    if (completionMethod === 'video' || completionMethod === 'reflection') {
      window.scrollTo({ 
        top: 0, 
        behavior: 'smooth' 
      });
    }

    // Handle post-completion logic (badge earned, navigation, etc.)
    if (data && data.badge_earned) {
      // Could show additional badge earned notification
      console.log('Badge earned!', data);
    }
  };

  // Handle manual completion for content-only modules
  const handleManualCompletion = async () => {
    try {
      const currentViewingTime = Math.floor((Date.now() - viewStartTime.current) / 1000);
      
      await updateContentProgress({
        moduleId: parseInt(moduleId),
        scroll_percentage: 100,
        reading_time: currentViewingTime
      }).unwrap();
      
      // Hide the complete button
      setShowCompleteButton(false);
      
      // Refetch module data to show completion
      refetch();
      
      // Auto-scroll to top to show completion banner
      window.scrollTo({ 
        top: 0, 
        behavior: 'smooth' 
      });
      
      handleModuleCompletion('content', {
        completion_method: 'content'
      });
    } catch (error) {
      console.error('Failed to complete module:', error);
    }
  };


  const handleNavigation = (direction) => {
    const navData = moduleData?.data?.navigation;
    if (direction === 'next' && navData?.next_module) {
      navigate(`/credentialing/modules/${navData.next_module.id}`);
    } else if (direction === 'prev' && navData?.prev_module) {
      navigate(`/credentialing/modules/${navData.prev_module.id}`);
    } else if (direction === 'dashboard') {
      navigate('/credentialing');
    }
  };

  const handleQuizSubmit = async (quizData) => {
    try {
      const result = await submitQuiz({
        moduleId: parseInt(moduleId),
        responses: quizData.responses
      }).unwrap();
      
      return result.data;
    } catch (error) {
      console.error('Quiz submission failed:', error);
      throw error;
    }
  };

  const handleQuizComplete = (quizResult) => {
    setShowQuiz(false);  // Hide quiz and return to module view
    
    // Refetch module data to update progress and completion status
    refetch();
    
    // Check if module was auto-completed via quiz
    if (quizResult.passed) {
      // Immediately unlock reflection option locally
      setUnlockedReflection(true);
      // Show reflection modal after a brief delay
      scheduleReflectionModal(1000);
    }
    if (quizResult.passed && quizResult.module_completed) {
      // Auto-scroll to top to show completion banner
      window.scrollTo({ 
        top: 0, 
        behavior: 'smooth' 
      });
      
      handleModuleCompletion('quiz', {
        badge_earned: quizResult.badge_earned,
        completion_method: quizResult.completion_method
      });
    }
  };

  const shouldShowReflectionButton = () => {
    const { module, progress } = moduleData?.data || {};
    if (!module) return false;
    // Hide if already submitted
    if (progress?.reflection_submitted_at) return false;

    // If user just unlocked via local actions, show immediately
    if (unlockedReflection) return true;

    // Only show if module supports reflection in some way
    const reflectionSupported =
      module.requires_reflection ||
      module.completion_method === 'reflection' ||
      !!module.reflection_prompt;
    if (!reflectionSupported) return false;

    // Passed quiz?
    const passedQuiz = progress?.quiz_status === 'passed';

    // Finished video?
    const hasVideo = !!(module.video_url && ReactPlayer.canPlay(module.video_url));
    const videoCompletedFlag = progress?.video_completed === true || module?.video_completed === true;
    const finishedVideo = hasVideo && videoCompletedFlag;

    return passedQuiz || finishedVideo;
  }

  const handleStartQuiz = () => {
    console.log("Progress:", progress);
    setShowQuiz(true);
  };

  const handleVideoEnded = () => {
    const { module, progress } = moduleData?.data || {};
    if (!module) return;
    const reflectionAlreadySubmitted = !!progress?.reflection_submitted_at;
    if (!reflectionAlreadySubmitted && !hasShownReflectionModalRef.current) {
  // Unlock and schedule delayed modal
  setUnlockedReflection(true);
  scheduleReflectionModal(1000);
    }
  };

  if (isLoading) {
    return (
      <div className="training-module">
        <ModuleSkeleton section={section} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="training-module">
        <div className="training-module__error">
          <h2>Unable to Load Module</h2>
          <p>There was an error loading this training module.</p>
          <div className="error-actions">
            <button 
              className={`${util.btn} ${util['btn--primaryGradient']} ${util.elevateHover}`}
              onClick={() => refetch()}
            >
              Try Again
            </button>
            <button 
              className={`${util.btn} ${util['btn--ghost']}`}
              onClick={() => navigate('/credentialing')}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { module, progress, navigation } = moduleData?.data || {};

  if (!module) {
    return (
      <div className="training-module">
        <div className="training-module__error">
          <h2>Module Not Found</h2>
          <p>The requested training module could not be found.</p>
          <button 
            className={`${util.btn} ${util['btn--primaryGradient']} ${util.elevateHover}`}
            onClick={() => navigate('/credentialing')}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="training-module">
      {/* Header */}
      <div className="training-module__header">
        <div className="header-navigation">
          <button 
            className={`${util.btn} ${util['btn--ghost']} ${util['btn-sm']}`}
            onClick={() => handleNavigation('dashboard')}
          >
            ← Back to Dashboard
          </button>
          
          <div className="module-meta">
            <span className="module-number">Module {module.order}</span>
            <span className="module-duration">
              ⏱️ ~{formatDuration(module.estimated_duration)}
            </span>
            {/* Reflection quick-launch button (always available if reflection not yet submitted and module supports reflection) */}
            {shouldShowReflectionButton() && (
              <button
                type="button"
                className="reflection-launch-btn"
                onClick={() => setShowReflectionModal(true)}
                aria-label="Add reflection"
                title="Add reflection"
              >
                <span className="reflection-launch-btn__icon" aria-hidden="true">✍️</span>
                <span className="reflection-launch-btn__text">Reflection</span>
              </button>
            )}
          </div>
        </div>

        <h1 className="module-title">{module.title}</h1>
        
        {module.summary && (
          <p className="module-summary">{module.summary}</p>
        )}

        {/* Learning Objectives */}
        {module.learning_objectives && module.learning_objectives.length > 0 && (
          <div className="learning-objectives">
            <h3>📋 Learning Objectives</h3>
            <ul>
              {module.learning_objectives.map((objective, index) => (
                <li key={index} className={`objective objective--${objective.level}`}>
                  {objective.text || objective}
                </li>
              ))}
            </ul>
          </div>
        )}

          {/* Progress Indicator */}
          {/* {(() => {
            // Determine if completion date is valid (exclude placeholder/epoch dates like 1970-01-01)
            if (!progress?.is_completed || !progress?.completed_at) return false;
            const completedDate = new Date(progress.completed_at);
            if (isNaN(completedDate)) return false;
            // Define a minimum plausible date (adjust as needed)
            const minValidDate = new Date('2024-01-01T00:00:00Z');
              // Treat anything before this as invalid (e.g., 1970-01-01 placeholders)
            return completedDate >= minValidDate;
          })() && (
            <div className="completion-banner">
              <span className="completion-icon">✅</span>
              <span>You completed this module on {new Date(progress.completed_at).toLocaleDateString()}</span>
            </div>
          )} */}
      </div>



      {/* Video Section with Progress Tracking */}
      {module.video_url && module.video_url.trim() && ReactPlayer.canPlay(module.video_url) && (!section || section === 'video' || section === 'all') && (
        <div className="training-module__video">
          <VideoProgressTracker
            videoUrl={module.video_url}
            moduleId={moduleId}
            completionThreshold={module.video_completion_threshold || 90}
            onModuleCompleted={handleModuleCompletion}
            onVideoEnded={handleVideoEnded}
            initialProgress={progress?.video_progress_percentage || 0}
            initialPosition={progress?.last_video_position || 0}
            isModuleCompleted={progress?.is_completed || false}
            hideProgressUI={true}
          />
        </div>
      )}

      {/* Content */}
      {(!section || section === 'content' || section === 'all') && (
      <div className="training-module__content">
        {/* Show viewing notice for content-only modules */}
        {/* {!progress?.is_completed && !module.has_quiz && !module.has_video && !module.requires_reflection && !showCompleteButton && (
          <div className="content-completion-section">
            <div className="viewing-time-notice">
              <span className="notice-icon">📖</span>
              <span>
                Read through this content - you will be able to mark it as complete at the bottom.
              </span>
            </div>
          </div>
        )} */}
        
        <div
          ref={contentRef}
          className="module-content"
          dangerouslySetInnerHTML={{ __html: module.content }}
        />
      </div>
      )}

      {/* Quiz Section */}
      {module.has_quiz && !showQuiz && (!section || section === 'quiz' || section === 'all') && (
        <div className="training-module__quiz-intro">
          <div className="quiz-intro-container">
            <h3>📋 Knowledge Check Quiz</h3>
            <p>Test your understanding of this module's content.</p>
            
            {progress?.quiz_status === 'passed' ? (
              <div className="quiz-status passed">
                <span className="status-icon">✅</span>
                <div className="status-info">
                  <p><strong>Quiz Passed!</strong></p>
                  <p>Score: {Math.round(progress.quiz_best_score)}% (Attempt #{progress.quiz_attempts})</p>
                  <button className={`${util.btn} ${util['btn--ghost']}`} onClick={handleStartQuiz}>
                    Take Quiz
                  </button>
                </div>
              </div>
            ) : progress?.quiz_status === 'attempted' ? (
              <div className="quiz-status attempted">
                <span className="status-icon">🔄</span>
                <div className="status-info">
                  <p><strong>Quiz Attempted</strong></p>
                  <p>Best Score: {Math.round(progress.quiz_best_score || 0)}% (Need {module.quiz_passing_score}%)</p>
                  <button className={`${util.btn} ${util['btn--primaryGradient']} ${util.elevateHover}`} onClick={handleStartQuiz}>
                    Take Quiz
                  </button>
                </div>
              </div>
            ) : (
              <div className="quiz-status not-started">
                <span className="status-icon">📝</span>
                <div className="status-info">
                  <p>Ready to test your knowledge?</p>
                  <p>Passing score: {module.quiz_passing_score}%</p>
                  <button className={`${util.btn} ${util['btn--primaryGradient']} ${util.elevateHover}`} onClick={handleStartQuiz}>
                    {!progress?.quiz_status || progress?.quiz_status === "not_started" ? 'Take Quiz' : 'View Quiz'}
                  </button>
                </div>
              </div>
            )}

            {module.requires_quiz_completion && progress?.quiz_status !== 'passed' && (
              <div className="quiz-requirement">
                <p><strong>Note:</strong> You must pass this quiz to complete the module.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quiz Component */}
      {showQuiz && module.has_quiz && (!section || section === 'quiz' || section === 'all') && (
        <div className="training-module__quiz">
          <ModuleQuiz
            quizData={module.quiz_data}
            moduleId={module.id}
            progress={progress}
            onQuizSubmit={handleQuizSubmit}
            onQuizComplete={handleQuizComplete}
          />
        </div>
      )}

      {/* Quiz Results Component */}
      {!showQuiz && section === 'quiz' && (
        <QuizResults 
          moduleId={moduleId}
          progress={progress}
          moduleData={module}
          onStartQuiz={handleStartQuiz}
        />
      )}
      
            {/* Reflection Section - show when section=reflection or when reflection should be shown inline */}
  {(section === 'reflection' || (module.completion_method !== 'manual' && section !== 'video' && section !== 'content' && !(module.video_url && ReactPlayer.canPlay(module.video_url)))) && (
        <div className="training-module__reflection">
          <ReflectionSubmission
            moduleId={moduleId}
            minLength={module.reflection_min_length || 100}
            placeholder={module.reflection_prompt || "Reflect on what you've learned in this module. How might you apply these concepts in your teaching practice?"}
            onReflectionSubmitted={handleModuleCompletion}
            initialReflection={progress?.reflection || ''}
            isSubmitted={!!progress?.reflection_submitted_at}
            submittedAt={progress?.reflection_submitted_at}
            isRequired={module.requires_reflection || module.completion_method === 'reflection'}
          />
        </div>
      )}

      {/* Complete Module Button - positioned at bottom for content-only modules(manually completed)*/}
  {!progress?.is_completed && !module.has_quiz && !module.has_video && !module.requires_reflection && module.completion_method !== 'manual' && (
        <div className="training-module__completion">
          <div className="manual-completion-section">
            <div className="completion-ready-notice">
              {showCompleteButton && (
                <>
                  <span className="ready-icon">✅</span>
                  <span>You've spent enough time reading this content. Ready to complete this module?</span>
                </>
              )}
              {!showCompleteButton && (
                <>
                  <span className="waiting-icon">⏳</span>
                  <span>Read through this content - you will be able to mark it as complete soon</span>
                </>
              )}
            </div>
            {showCompleteButton && (
              <button 
                className={`${util.btn} ${util['btn--successGradient']} ${util.elevateHover} complete-module-btn`}
                onClick={handleManualCompletion}
              >
                ✓ Complete Module
              </button>
            )}
          </div>
        </div>
      )}

      {/* Navigation removed by design */}

      {/* Reflection Modal (shown after video completes) */}
      {showReflectionModal && (
        <InfoModal>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Module Reflection</h3>
              <button onClick={() => setShowReflectionModal(false)} className={`${util.btn} ${util['btn--ghost']} ${util['btn-sm']}`}>Not now</button>
            </div>
            <p style={{ marginTop: 0 }}>Would you like to write a reflection on what you learned?</p>
            <ReflectionSubmission
              moduleId={moduleId}
              minLength={module.reflection_min_length || 100}
              placeholder={module.reflection_prompt || "Try to describe in one or two sentences what you learned and how you will apply this new knowledge in your work."}
              onReflectionSubmitted={(data) => {
                setShowReflectionModal(false);
                handleModuleCompletion('reflection', data);
              }}
              initialReflection={progress?.reflection || ''}
              isSubmitted={!!progress?.reflection_submitted_at}
              submittedAt={progress?.reflection_submitted_at}
              isRequired={module.requires_reflection || module.completion_method === 'reflection'}
            />
          </div>
        </InfoModal>
      )}
    </div>
  );
};

export default BadgeModule;
